// grid info [<name>] [--json]  — M8a.4
//
// Shows detailed entity metadata: display_name, kind, org, space, owner,
// created_at, updated_at, last_deploy.
//
// Resolution (spec v17 §13.4):
//   - No <name> arg, in a linked dir:  uses link.json entity_id + validates
//     active-org mismatch (§5).
//   - <name> arg given: passes the name directly as the :id path segment;
//     the server resolves UUID or display_name.
//
// Read path:
//   GET /api/v2/runtimes/:id → V2RuntimeDetailResponse
//   Prints human-readable metadata; --json emits the API response plus the
//   flat `owner_handle` / `org_slug` aliases (backlog 351).
//
// Deferred from M8a (per audit 2026-04-29 verdict on PR #165):
//   --builds   — list recent build history. Spec v17 §13.4 / audit checklist A4.
//   --usage    — show resource and AI-budget usage. Spec v17 §13.4 / audit A4.
//   --runs     — list recent agent runs (agent-tier entities only). Spec v17 §13.4.
//   --rename   — rename the entity. Removed in this rework: spec v17 §16
//                describes a slug-rename (URL change + 90-day redirect) that
//                requires a server endpoint not registered in
//                `docs/plans/v2-endpoint-expansion-plan.md` §67-78. Decision
//                012's "What this decision does NOT decide" section leaves
//                the rename UX open. To re-introduce, land the server-side
//                PATCH first, update the v2 expansion plan, and reconcile
//                the §13.4 / §16 spec divergence (M9 docs cleanup).
// All four flags are tracked in `docs/backlog/037-cli-info-deferred-flags.md`.

import { Command } from 'commander';
import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';
import { parse as parseYaml } from 'yaml';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { requireLink } from '../services/link.js';
import { isGlobalEntityRef } from '../services/entity-ref.js';
import { resolveBareSlugAcrossOrgs, bareSlugNotFoundMessage } from '../services/resolve-bare-slug.js';
import { CloudGridError } from '../utils.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';
import { handleV2Error } from '../handle-error.js';
import type { V2RuntimeDetailResponse } from './status.js';
import { formatRelativeTime } from './status.js';
import { visibilityDescription } from '../services/visibility-format.js';
import {
  detectVectorCapability,
  type VectorCapability,
} from '../services/vector-capability.js';

// ── Entity resolution ─────────────────────────────────────────────────────

/**
 * Resolve the entity identifier to use as the :id path segment.
 *
 * No name arg → reads link.json and returns the link's entity_id (a
 * server-issued UUID). ADR 036 V2: the UUID is globally addressable by
 * membership, so the old link-vs-active-org check is gone — `info` in a
 * linked dir works regardless of which org the CLI's active org points at.
 *
 * Name arg that is a UUID or `org/slug` ref → returns as-is; both are
 * globally addressable and the server resolves them directly.
 *
 * Name arg that is a bare slug (CLI 0.9.3) → searches across every org you
 * belong to and returns the unique match's UUID, instead of scoping silently
 * to the active org. Throws `MultipleEntityMatchesError` on more than one
 * match and `CloudGridError` when no org has the slug.
 */
async function resolveEntityId(
  nameArg: string | undefined,
  client: IV2Client,
): Promise<string> {
  if (nameArg === undefined) return requireLink().entity_id;
  const arg = nameArg.trim();
  if (isGlobalEntityRef(arg)) return arg;
  const match = await resolveBareSlugAcrossOrgs(arg, client);
  if (!match) {
    throw new CloudGridError(bareSlugNotFoundMessage(arg, 'info'));
  }
  return match.entity_id;
}

// ── Local capability detection (BL579 M4) ─────────────────────────────────

/**
 * Read the project's own `cloudgrid.yaml` (when present in `cwd`) and detect
 * the managed vector substrate. The runtime-detail API carries no capability
 * declarations and BL579 M4 is a CLI-only deliverable, so the yaml is the
 * source of truth for "does this entity declare vector?".
 *
 * Tolerant: a missing or malformed yaml resolves to `null` rather than
 * throwing — `info` must still print the server-side metadata.
 */
function readVectorCapabilityFromCwd(cwd: string): VectorCapability | null {
  const yamlPath = join(cwd, 'cloudgrid.yaml');
  if (!existsSync(yamlPath)) return null;
  let parsed: unknown;
  try {
    parsed = parseYaml(readFileSync(yamlPath, 'utf-8'));
  } catch {
    return null;
  }
  return detectVectorCapability(parsed);
}

// ── Human-readable rendering ──────────────────────────────────────────────

function renderInfo(
  entity: V2RuntimeDetailResponse,
  vector: VectorCapability | null,
): string {
  const label = entity.display_name || entity.name;
  const lines: string[] = [];

  lines.push(`${label}  (${entity.kind})`);
  lines.push(`  Org:         ${entity.org.slug}`);

  const spaceLabel = entity.space != null ? String(entity.space) : '—';
  lines.push(`  Space:       ${spaceLabel}`);

  lines.push(`  Owner:       ${entity.owner ? entity.owner.email : '—'}`);
  lines.push(`  Created:     ${formatRelativeTime(entity.created_at)}`);
  lines.push(`  Updated:     ${formatRelativeTime(entity.updated_at)}`);

  if (entity.last_deploy !== null) {
    const ago = formatRelativeTime(entity.last_deploy.deployed_at);
    lines.push(`  Last deploy: ${entity.last_deploy.status}  ${ago}`);
  } else {
    lines.push(`  Last deploy: —`);
  }

  // Smoke Finding 4 — surface the live URL and visibility so the user can
  // recover both after closing the plug terminal. URL is the server-composed
  // serving URL (decision 007's entityUrl, already on the response).
  lines.push(`  URL:         ${entity.url || '—'}`);
  if (entity.visibility) {
    lines.push(
      `  Visibility:  ${entity.visibility} · ${visibilityDescription(entity.visibility)}`,
    );
  }

  // BL579 M4 — managed vector substrate. Show the capability + the env var the
  // entity's code reads at runtime; never a credential. Only present when the
  // project's cloudgrid.yaml declares it.
  if (vector) {
    lines.push(`  Vector:      ${vector.engine} — env: ${vector.envVar}`);
  }

  return lines.join('\n');
}

// ── JSON output shape ─────────────────────────────────────────────────────

/**
 * The `--json` payload: the raw detail response plus two flat aliases for
 * scripting convenience (backlog 351).
 *
 * The detail response nests the owner handle at `owner.slug` and the org slug
 * at `org.slug`. A script reaching for `jq .owner_handle` / `jq .org_slug`
 * would otherwise get `null` — not because the data is missing, but because
 * those top-level paths don't exist. We surface both as flat fields without
 * touching the nested shape, so existing consumers keep working.
 *
 * `owner_handle` is null when the owner's user record was purged (account
 * deletion) — the API returns `owner: null` in that case, by design.
 */
export type InfoJson = V2RuntimeDetailResponse & {
  owner_handle: string | null;
  org_slug: string;
  /**
   * BL579 M4 — the managed vector substrate, read from the local
   * cloudgrid.yaml. `null` when the entity declares none, or when `info`
   * targets a non-local entity (a name-arg lookup, where the local yaml
   * cannot be trusted to describe the shown entity). Carries the engine + the
   * env var the entity reads, never the credential.
   */
  vector: { engine: string; env_var: string; dim?: number } | null;
};

function toInfoJson(
  entity: V2RuntimeDetailResponse,
  vector: VectorCapability | null,
): InfoJson {
  return {
    ...entity,
    owner_handle: entity.owner ? entity.owner.slug : null,
    org_slug: entity.org.slug,
    vector: vector
      ? {
          engine: vector.engine,
          env_var: vector.envVar,
          ...(vector.dim !== undefined ? { dim: vector.dim } : {}),
        }
      : null,
  };
}

// ── Command options type ──────────────────────────────────────────────────

export interface InfoOpts {
  json?: boolean;
  output?: string;
}

// ── Command implementation ────────────────────────────────────────────────

/**
 * Core implementation. Accepts an optional `client` override for unit tests.
 * In production, the client is created via `getV2Client()`.
 */
export async function runInfo(
  nameArg: string | undefined,
  opts: InfoOpts,
  client?: IV2Client,
  cwd: string = process.cwd(),
): Promise<void> {
  const apiClient = client ?? (await getV2Client());
  const resolvedId = await resolveEntityId(nameArg, apiClient);

  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  // encodeURIComponent keeps an `org/slug` ref one path segment (`org%2Fslug`).
  const entity = await apiClient.get<V2RuntimeDetailResponse>(
    `/api/v2/runtimes/${encodeURIComponent(resolvedId)}`,
  );

  // BL579 M4 — surface the managed vector substrate, but ONLY for the linked-dir
  // invocation (no name arg). With a name arg, `info` is a directory-independent
  // remote lookup, so the local cloudgrid.yaml cannot be trusted to describe the
  // shown entity; we skip the row rather than risk a false claim.
  const vector = nameArg === undefined ? readVectorCapabilityFromCwd(cwd) : null;

  out.json(() => toInfoJson(entity, vector));
  out.human(() => renderInfo(entity, vector));
}

// ── Commander command export ──────────────────────────────────────────────

export const infoCommand = addOutputOption(
  new Command('info')
    .description('Show detailed entity metadata')
    .argument('[name]', 'Entity name or UUID (uses linked entity if omitted)')
    .option('--json', 'Machine-readable JSON output (alias of -o json)'),
).action(async (name: string | undefined, opts: InfoOpts) => {
  try {
    await runInfo(name, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
